import { useState, type ReactNode } from 'react'
import { useMutation } from '@w/react-query'
import { Button } from '@w/ui/components/button'
import { DatabaseIcon } from '@w/ui/components/icon'
import { Input } from '@w/ui/components/input'
import { Dialog, DialogPopup, DialogTitle } from '@w/ui/components/dialog'
import type { SlashMenuItem } from '@w/content-blocks-editor'
import { useNavigate } from '../router/flashcards-fe.router-bindings'
import type { SpaceResource } from '../router/spaces-resources'
import { databaseRootId, databaseSpaceRootOptions } from './database-space'

/**
 * The editor's / Database item (Notion /database; flashcards-editor-home.requirements.md 2026-11-02): it asks for a name,
 * creates the database in the page's space and opens it. Offered only to space editors (Edit on the space root, from the
 * caller's `levels`), the level the schema change is accepted at (custom-databases.spec.md).
 */
export function useNewDatabase({ resource, levels, beforeCreate }: { resource: SpaceResource; levels: Readonly<Record<string, number>>; beforeCreate: () => Promise<void> }): { item: SlashMenuItem | null; dialog: ReactNode } {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const level = levels[databaseRootId(resource)] ?? 0
  const mutation = useMutation({ mutationFn: async (table: string) => {
    await beforeCreate()
    await resource.customDb.createDatabase({ table, spaceId: await databaseSpaceRootOptions(resource).queryFn() })
    setOpen(false)
    setName('')
    navigate({ to: '/$workspaceId/spaces/$spaceId/databases/$table', params: { workspaceId: resource.space.workspaceId, spaceId: resource.space.id, table } })
  } })
  function create() { if (name.trim() && !mutation.isPending) mutation.mutate(name) }
  const item: SlashMenuItem | null = level >= 2
    ? { title: 'Database', keywords: ['database', 'table', 'db'], section: 'Basic', icon: <DatabaseIcon />, onSelect: () => { mutation.reset(); setOpen(true) } }
    : null
  const dialog = <Dialog open={open} onOpenChange={setOpen}><DialogPopup className="flex flex-col gap-4 p-6"><DialogTitle>New database</DialogTitle>
    <label htmlFor="database-name" className="flex flex-col gap-2">Database name<Input id="database-name" data-testid="database-name" value={name} onChange={event => { setName(event.target.value) }} onKeyDown={event => { if (event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); create() } }} /></label>
    {mutation.error ? <p role="alert">{String(mutation.error)}</p> : null}
    <div className="flex gap-2"><Button onClick={() => { setOpen(false) }}>Cancel</Button><Button data-testid="database-create" disabled={!name.trim() || mutation.isPending} onClick={create}>Create database</Button></div>
  </DialogPopup></Dialog>
  return { item, dialog }
}
